/*
 * Copyright (c) CIRROM 2025.
 */

import React from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Typography,
  CardHeader,
} from "@mui/material";
import { UserActivityByField as UserActivityByFieldType } from "../../api/dashboards";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import styled from "@emotion/styled";
import { Pie } from "react-chartjs-2";
import { useTheme } from "@mui/material/styles";
import "chart.js/auto";

const ChartContainer = styled.div`
  height: 400px;
`;

const UserSelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

interface User {
  id: number;
  name: string;
}

interface UserActivityByFieldProps {
  userActivityByField: UserActivityByFieldType[];
  isLoading: boolean;
  selectedUserId: number | "all";
  uniqueUsers: User[];
}

export const UserActivityByField: React.FC<UserActivityByFieldProps> = ({
  userActivityByField,
  isLoading,
  selectedUserId,
  uniqueUsers,
}) => {
  const theme = useTheme();

  const processChartData = () => {
    if (!userActivityByField || userActivityByField.length === 0) {
      return { labels: [], datasets: [] };
    }

    let filteredData = userActivityByField;
    if (selectedUserId !== "all") {
      filteredData = userActivityByField.filter(
        (item) => item.user_id === selectedUserId
      );
    }

    if (filteredData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const fieldData = filteredData.reduce((acc, item) => {
      if (!acc[item.field]) {
        acc[item.field] = 0;
      }
      acc[item.field] += item.change_count;
      return acc;
    }, {});

    const fields = Object.keys(fieldData);
    const values = Object.values(fieldData);

    // Generate colors for pie slices
    const colors = fields.map((_, index) => {
      const hue = (index * 360) / fields.length;
      return `hsl(${hue}, 70%, 60%)`;
    });

    return {
      labels: fields,
      datasets: [
        {
          label:
            selectedUserId !== "all"
              ? "Changes by Field"
              : "Total Changes by Field",
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((color) => color.replace("60%", "50%")),
          borderWidth: 2,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          boxWidth: 12,
          padding: 15,
        },
      },
      title: {
        display: true,
        text:
          selectedUserId !== "all"
            ? `Field Distribution - ${
                uniqueUsers.find((u) => u.id === selectedUserId)?.name || "User"
              }`
            : "Team Field Distribution",
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} changes (${percentage}%)`;
          },
        },
      },
    },
  };

  const chartData = processChartData();

  return (
    <>
      <Grid justifyContent="space-between" container spacing={6}>
        <Grid>
          <Typography variant="h4" gutterBottom>
            Field Distribution
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={6}>
        <Grid
          size={{
            xs: 12,
            lg: 12,
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MiraCard>
              <CardHeader title="Changes by Data Field" />
              <MiraCardContent>
                <ChartContainer>
                  <Pie data={chartData} options={chartOptions} />
                </ChartContainer>
              </MiraCardContent>
            </MiraCard>
          )}
        </Grid>
      </Grid>
    </>
  );
};
